import React from "react";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import { Capacitor } from "@capacitor/core";
import { CapacitorUpdater } from "@capgo/capacitor-updater";
import App from "./App";
import "./index.css";
import "./config/setup-api-fetch";
import { initializeCapacitor } from "./lib/capacitor-init";
// Initialize i18next before rendering — must be imported before App
import "./lib/i18n";

// OTA splash helpers (defined inline in index.html)
const splash = (window as any).__otaSplash as {
  show(msg?: string): void;
  hide(): void;
  status(msg: string): void;
  progress(pct: number): void;
} | undefined;

// Signal to CapacitorUpdater that the app loaded successfully.
// Must be called on every launch — if a new bundle crashes before this fires,
// the plugin auto-rolls back to the previous working bundle.
if (Capacitor.isNativePlatform()) {
  CapacitorUpdater.notifyAppReady();
  // Tag Sentry with the active OTA bundle version for crash attribution
  CapacitorUpdater.current().then(({ bundle }) => {
    const otaVersion = (!bundle || bundle.id === "builtin" || !bundle.version)
      ? "native"
      : bundle.version;
    Sentry.setTag("ota_bundle_version", otaVersion);
  }).catch(() => {});

  // --- OTA update listeners (silent, apply on next launch) ---
  // Downloads happen in the background. When complete, stage the bundle via
  // CapacitorUpdater.next() so it activates on the next cold start.
  // No reload, no popup — completely invisible to the user.
  CapacitorUpdater.addListener("download", (ev: any) => {
    const pct = ev?.percent ?? 0;
    if (pct % 25 === 0 || pct >= 99) console.log(`[OTA] Downloading: ${pct}%`);
  });

  CapacitorUpdater.addListener("downloadComplete", async (state: any) => {
    const bundle = state?.bundle;
    if (bundle?.id && bundle?.version) {
      console.log(`[OTA] Download complete: ${bundle.version} — staged for next launch`);
      try {
        await CapacitorUpdater.next({ id: bundle.id });
      } catch (err) {
        console.warn("[OTA] Failed to stage bundle:", err);
      }
    }
  });

  CapacitorUpdater.addListener("downloadFailed", (ev: any) => {
    console.warn("[OTA] Download failed:", ev);
  });

  CapacitorUpdater.addListener("updateAvailable", async (state: any) => {
    const bundle = state?.bundle;
    if (bundle?.id && bundle?.version) {
      console.log(`[OTA] Update available: ${bundle.version} — staged for next launch`);
      try {
        await CapacitorUpdater.next({ id: bundle.id });
      } catch (err) {
        console.warn("[OTA] Failed to stage bundle:", err);
      }
    }
  });

  // Link Firebase user to OTA device so beta testers auto-route to beta channel.
  // Call setCustomId immediately with any already-signed-in user so the UID is
  // set BEFORE the auto-update check fires (onAuthStateChanged fires async and
  // can be too late — currentUser is synchronously available if already authed).
  import("firebase/auth").then(({ getAuth, onAuthStateChanged }) => {
    const auth = getAuth();
    // Sync: if already signed in, set it now before the update check runs
    if (auth.currentUser) {
      CapacitorUpdater.setCustomId({ customId: auth.currentUser.uid });
    }
    // Async: keep it updated on sign-in/sign-out
    onAuthStateChanged(auth, (user) => {
      CapacitorUpdater.setCustomId({ customId: user?.uid ?? "" });
    });
  }).catch(() => {});
}

// Auto-reload once when a JS chunk fails to load (e.g. after a new deployment
// replaced old content-hashed filenames). The reload fetches fresh index.html
// which references the correct new chunk names.
window.addEventListener('unhandledrejection', (event) => {
  const msg: string = (event.reason as Error)?.message ?? '';
  if (
    (event.reason as Error)?.name === 'ChunkLoadError' ||
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('error loading dynamically imported module')
  ) {
    if (!sessionStorage.getItem('chunk-reload-attempted')) {
      sessionStorage.setItem('chunk-reload-attempted', 'true');
      window.location.reload();
    }
  }
});

// Suppress ResizeObserver errors globally
// This is a known browser warning from Radix UI components (Select, ScrollArea, etc.)
// It's not a real error - just a notification that ResizeObserver callbacks took longer than 1 frame
// See: https://github.com/radix-ui/primitives/issues/1297
const resizeObserverErr = window.ResizeObserver;
window.ResizeObserver = class ResizeObserver extends resizeObserverErr {
  constructor(callback: ResizeObserverCallback) {
    super((entries, observer) => {
      // Wrap callback in requestAnimationFrame to prevent the error
      requestAnimationFrame(() => {
        callback(entries, observer);
      });
    });
  }
};

// Initialize Sentry error tracking and performance monitoring
// Use mobile DSN if running on Capacitor, otherwise use frontend DSN
const isNativePlatform = Capacitor.isNativePlatform();
const dsn = isNativePlatform
  ? (import.meta.env.VITE_SENTRY_DSN_MOBILE || import.meta.env.VITE_SENTRY_DSN)
  : (import.meta.env.VITE_SENTRY_DSN_FRONTEND || import.meta.env.VITE_SENTRY_DSN);

console.log('[Sentry] Initializing...', {
  platform: isNativePlatform ? 'mobile' : 'web',
  dsnConfigured: !!dsn,
  dsnPreview: dsn?.substring(0, 50) + '...',
});

if (dsn) {
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE || 'development',
    // Get release version from package.json or git commit
    release: import.meta.env.VITE_APP_VERSION || 'unknown',

    // Performance monitoring
    tracesSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 1.0,

    // Session replay - only enabled for web builds (not Capacitor)
    // Disabled for native apps as web workers don't work in Capacitor WebView
    replaysSessionSampleRate: isNativePlatform ? 0 : (import.meta.env.MODE === 'production' ? 0.1 : 1.0),
    replaysOnErrorSampleRate: isNativePlatform ? 0 : 1.0,

    // Filter out known noisy errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'chrome-extension://',
      'moz-extension://',
      'safari-extension://',
      // Network errors that don't need tracking
      'NetworkError',
      'Failed to fetch',
      'Load failed',
      // Common React errors during development
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      // Third-party errors
      /MetaMask/i,
      /web3/i,
    ],

    // Additional context
    beforeSend(event, hint) {
      // Add custom context
      const error = hint.originalException;
      if (error && typeof error === 'object') {
        event.tags = {
          ...event.tags,
          platform: isNativePlatform ? 'mobile' : 'web',
          capacitor_platform: Capacitor.getPlatform(),
        };
      }
      return event;
    },

    // Only add browser tracing and session replay for web (not Capacitor)
    // Browser tracing adds headers that can cause CORS issues in native apps
    integrations: [
      ...(isNativePlatform ? [] : [Sentry.browserTracingIntegration()]),
      ...(isNativePlatform ? [] : [
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        }),
      ]),
    ],
  });
  console.log('[Sentry] Initialized successfully ✅', {
    mode: import.meta.env.MODE,
    platform: Capacitor.getPlatform(),
    tracesSampleRate: import.meta.env.MODE === 'production' ? '10%' : '100%',
    replaysSessionSampleRate: isNativePlatform ? 'Disabled (native app)' : (import.meta.env.MODE === 'production' ? '10%' : '100%'),
    replaysOnErrorSampleRate: isNativePlatform ? 'Disabled (native app)' : '100%',
  });
} else {
  console.warn('[Sentry] Not initialized - DSN not configured');
}

// Initialize Capacitor plugins
initializeCapacitor();

// Note: Shakebugs is now initialized in the ShakeFeedback component
// to properly integrate with user authentication and React lifecycle

createRoot(document.getElementById("root")!).render(<App />);

// Hide OTA splash once React has mounted
requestAnimationFrame(() => {
  splash?.hide();
});
